import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/browser';

import ImageConfigurator from '../../../../../components/ImageConfigurator';
import useStorage, { StorageProcessState } from '../../../../../hooks/useStorage';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';

import { storagePaths } from '../../../../../utils/firebasePaths';
import { SectionLabel } from './styles';
import texts from './texts.json';

const PhotoUpload = ({
  noteId,
  photoUrl,
  onChange,
  onUploadingChange,
  isReadOnly,
}) => {
  const { uploadAttachment } = useStorage();
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();
  const [imagePreview, setImagePreview] = useState(photoUrl || '');
  const [fileToUpload, setFileToUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileToUpload = useCallback(async (file) => {
    setFileToUpload(file);
    if (!file) return;

    const blobUrl = URL.createObjectURL(file);
    const ext = file.name.split('.').pop() || 'jpg';
    const baseName = file.name.slice(0, file.name.length - ext.length - 1) || 'photo';

    setIsUploading(true);
    onUploadingChange(true);
    try {
      const result = await uploadAttachment(
        blobUrl,
        ext,
        `${storagePaths.RELEASE_NOTE}/${noteId}`,
        baseName,
        file.type,
        '',
        true,
      );
      if (result.state === StorageProcessState.SUCCESS) {
        onChange(result.publicUrl);
        if (isComponentMountedRef.current) {
          setImagePreview(result.publicUrl);
        }
      } else {
        showToast(texts.uploadError, { error: true });
      }
    } catch (error) {
      Sentry.captureException(error);
      showToast(texts.uploadError, { error: true });
    } finally {
      if (isComponentMountedRef.current) {
        setIsUploading(false);
        onUploadingChange(false);
        URL.revokeObjectURL(blobUrl);
      }
    }
  }, [noteId, uploadAttachment, onChange, onUploadingChange, showToast, isComponentMountedRef]);

  if (isReadOnly && !photoUrl) return null;

  return (
    <div>
      <SectionLabel>{texts.sectionTitle}</SectionLabel>
      <ImageConfigurator
        imageUrl={imagePreview}
        setImagePreview={setImagePreview}
        fileToUpload={fileToUpload}
        setFileToUpload={handleFileToUpload}
        isReadOnly={isReadOnly || isUploading}
      />
    </div>
  );
};

PhotoUpload.propTypes = {
  noteId: PropTypes.string.isRequired,
  photoUrl: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onUploadingChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

PhotoUpload.defaultProps = {
  photoUrl: null,
  onUploadingChange: () => {},
  isReadOnly: false,
};

export default PhotoUpload;
